import {
  ErrorComponent,
  Link,
  rootRouteId,
  useMatch,
  useRouter,
} from '@tanstack/react-router'
import type { ErrorComponentProps } from '@tanstack/react-router'
import * as React from 'react'
import * as Sentry from '@sentry/tanstackstart-react'
import { isSentryEnabledClient } from '~/lib/sentry/enabled-client'
import { withLocale } from '~/lib/utils/with-locale'
import { NotFound } from './NotFound'

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  const router = useRouter()
  const isRoot = useMatch({
    strict: false,
    select: (state) => state.id === rootRouteId,
  })

  // Check if this is a NotFoundError - if so, render NotFound component instead
  // Server function errors might come through as HTTP responses with status codes
  const isNotFoundError = React.useMemo(() => {
    if (!error || typeof error !== "object") return false;
    
    // Check for NotFoundError by name
    if (error.name === "NotFoundError" || error.constructor?.name === "NotFoundError") {
      return true;
    }
    
    // Check for 404 status code (server function errors might have this)
    if ("status" in error && error.status === 404) {
      return true;
    }
    
    // Check for statusCode property
    if ("statusCode" in error && error.statusCode === 404) {
      return true;
    }
    
    // Check error message - TanStack Start serializes notFound() as JSON in the message
    if (error.message && typeof error.message === "string") {
      // Try to parse as JSON (TanStack Start serializes notFound() as {"isNotFound":true})
      try {
        const parsed = JSON.parse(error.message);
        if (parsed && typeof parsed === "object" && parsed.isNotFound === true) {
          return true;
        }
      } catch {
        // Not JSON, check for text patterns
      }
      
      // Check for text patterns
      const msg = error.message.toLowerCase();
      if (msg.includes("not found") || msg.includes("404")) {
        return true;
      }
    }
    
    return false;
  }, [error]);

  // If it's a not-found error, render the NotFound component
  if (isNotFoundError) {
    return <NotFound />;
  }

  // Capture error in Sentry (only in production/staging, not during development)
  // Skip expected authentication errors to avoid spamming error logs
  React.useEffect(() => {
    const isDevelopment = import.meta.env.MODE === 'development';
    if (!isDevelopment && isSentryEnabledClient()) {
      // Don't capture ExpectedAuthError - these are expected user errors
      if (error && typeof error === "object" && "name" in error && error.name === "ExpectedAuthError") {
        return;
      }
      Sentry.captureException(error);
    }
  }, [error])

  console.error(error)

  return (
    <div className="min-w-0 flex-1 p-4 flex flex-col items-center justify-center gap-6">
      <ErrorComponent error={error} />
      <div className="flex gap-2 items-center flex-wrap">
        <button
          onClick={() => {
            router.invalidate()
          }}
          className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded text-white uppercase font-extrabold`}
        >
          Try Again
        </button>
        {isRoot ? (
          <Link
            to={withLocale("/")}
            className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded text-white uppercase font-extrabold`}
          >
            Home
          </Link>
        ) : (
          <Link
            to={withLocale("/")}
            className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded text-white uppercase font-extrabold`}
            onClick={(e) => {
              e.preventDefault()
              window.history.back()
            }}
          >
            Go Back
          </Link>
        )}
      </div>
    </div>
  )
}
